/* eslint-disable react-hooks/exhaustive-deps */

import React, {
  useState, useEffect, useRef, useMemo
} from 'react'
import { useClickAway, useKeyPressEvent } from 'react-use'
import PropTypes from 'prop-types'
import TextareaAutosize from 'react-autosize-textarea'
import styled from 'styled-components/macro'
import { useFirstMountState, useDoubleClick } from 'hooks'

const useInlineEdit = ref => {
  const [isEditing, setIsEditing] = useState(false)
  const [isKeyEnterPressed, setKeyEnterPressed] = useState(false)
  useKeyPressEvent(
    'Enter',
    () => setKeyEnterPressed(true),
    () => setKeyEnterPressed(false)
  )

  useClickAway(ref, () => {
    setIsEditing(false)
  })

  useEffect(() => {
    if (isEditing && isKeyEnterPressed) setIsEditing(false)
  }, [isEditing, isKeyEnterPressed])

  return [isEditing, setIsEditing]
}

export default WrappedComponent => {
  const useInlineEditWrapper = ({
    children,
    onChange,
    onFocus,
    onBlur,
    focusTrigger,
    disableDoubleClick,
    ...props
  }) => {
    const elementToEdit = useRef(null)
    const inputRef = useRef(null)
    const [value, setValue] = useState(children)
    const [isEditing, setIsEditing] = useInlineEdit(elementToEdit)
    const isFirstMount = useFirstMountState();

    const computeEditingClasses = isEditing => {
      if (isEditing) return 'isEditable--editing'
      return 'isEditable'
    }
    const isEditingClasses = useMemo(() => computeEditingClasses(isEditing), [
      isEditing
    ])

    const handleChange = e => {
      setValue(e.target.value)
      onChange(e)
    }

    // sets caret to end
    // https://stackoverflow.com/posts/35951917/revisions
    const handleFocus = e => {
      const val = e.target.value;
      e.target.value = '';
      e.target.value = val;
      onFocus(e)
    }

    useEffect(() => {
      if (!isFirstMount) {
        setIsEditing(true)
      }
    }, [focusTrigger])

    useEffect(() => {
      if (inputRef && inputRef.current && isEditing) {
        inputRef.current.focus()
      }
    }, [inputRef, isEditing])

    useEffect(() => {
      if (!isEditing) onBlur()
    }, [isEditing])

    useDoubleClick({
      onDoubleClick: () => {
        if (!disableDoubleClick) setIsEditing(true)
      },
      ref: elementToEdit,
      latency: 250
    });

    return (
      <InlineEditWrapper
        ref={elementToEdit}
        className={isEditingClasses}
        role="button"
      >
        <WrappedComponent {...props}>
          {isEditing ? (
            <TextareaAutosize
              ref={inputRef}
              value={value}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={onBlur}
              style={{ all: 'unset', width: '100%' }}
            />
          ) : (
            value
          )}
        </WrappedComponent>
      </InlineEditWrapper>
    )
  }

  useInlineEditWrapper.propTypes = {
    children: PropTypes.node,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    focusTrigger: PropTypes.bool,
    disableDoubleClick: PropTypes.bool,
  }

  useInlineEditWrapper.defaultProps = {
    children: 'Click me to edit',
    onChange: () => { },
    onFocus: () => { },
    onBlur: () => { },
    focusTrigger: false,
    disableDoubleClick: false,
  }

  const InlineEditWrapper = styled.div``

  return useInlineEditWrapper
}
